/**
 * consent-dialog.ts — One-time Beta/Alpha ToS click-through dialog.
 *
 * Exports:
 *   requireConsentIfNeeded(releaseStage): Promise<boolean>
 *     — Resolves true when the user has consented (or stage does not require consent).
 *     — Resolves false when the user declines.
 *
 * Design doc: docs/design/alpha-to-commercial-pathway-260601.md §3 + §5.5.
 * ToS skeleton:  docs/legal/tos-beta-clickthrough-260601.md
 *
 * a11y compliance:
 *   - role="dialog" + aria-modal="true" (modal dialog pattern)
 *   - aria-labelledby pointing to the dialog title element
 *   - aria-describedby pointing to the body element
 *   - Focus trap: Tab / Shift+Tab cycle within focusable dialog elements
 *   - ESC closes as "decline" (does not persist consent)
 *   - Focus returns to the triggering element on close
 *
 * Persistence: consent stored in cookie "adatool_consent=<version>" (1-year expiry)
 * per design doc §5.5. Anonymous visitors get cookie-only persistence; signed-in
 * users get cookie + server-side DB row (Phase 3+).
 *
 * Current consent version: "1". When ToS text is revised (Phase 4+ legal review),
 * bump this constant to re-trigger the dialog for users who consented to v1.
 */
/** Current ToS consent version. Bump when ToS text changes materially. */
const CONSENT_VERSION = '1';
/** Cookie name per design doc §5.5. */
const CONSENT_COOKIE = 'adatool_consent';
/** Release stages that require a click-through consent gate. */
const CONSENT_REQUIRED_STAGES = new Set(['ALPHA', 'BETA']);
// ─── Cookie helpers ────────────────────────────────────────────────────────────
function _readConsentCookie() {
    const prefix = CONSENT_COOKIE + '=';
    for (const part of document.cookie.split(';')) { // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        const trimmed = part.trimStart();
        if (trimmed.startsWith(prefix)) {
            return trimmed.slice(prefix.length);
        }
    }
    return null;
}
function _writeConsentCookie(version) {
    // 1-year expiry per design doc §5.5.
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(version)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
}
function _hasValidConsent() {
    const stored = _readConsentCookie();
    return stored === CONSENT_VERSION;
}
// ─── Dialog rendering + focus trap ────────────────────────────────────────────
const TITLE_ID = 'adatool-consent-dialog-title';
const BODY_ID = 'adatool-consent-dialog-body';
const DIALOG_ID = 'adatool-consent-dialog';
/**
 * Build and insert the dialog DOM into document.body.
 * Returns the dialog element.
 */
function _buildDialog() {
    const overlay = document.createElement('div'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    overlay.id = DIALOG_ID;
    // WHY: position:fixed + inset:0 ensures the backdrop covers the full viewport
    // regardless of scroll position, blocking interaction with the page below.
    overlay.style.cssText = [
        'position:fixed',
        'inset:0',
        'z-index:2000', // above the z-modal=1000 used by existing modals
        'background:rgba(0,0,0,0.6)',
        'display:flex',
        'justify-content:center',
        'align-items:center',
        'padding:16px',
    ].join(';');
    const dialog = document.createElement('div'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', TITLE_ID);
    dialog.setAttribute('aria-describedby', BODY_ID);
    dialog.style.cssText = [
        'background:var(--surface,#fff)',
        'border-radius:12px',
        'padding:32px',
        'max-width:600px',
        'width:100%',
        'max-height:90vh',
        'max-height:90dvh',
        'overflow-y:auto',
        'box-shadow:0 8px 32px rgba(0,0,0,0.25)',
        'position:relative',
    ].join(';');
    const title = document.createElement('h2'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    title.id = TITLE_ID;
    title.style.cssText = 'font-size:20px;font-weight:700;margin:0 0 16px;color:var(--text,#111)';
    title.textContent = 'Terms of Use';
    const body = document.createElement('div'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    body.id = BODY_ID;
    body.style.cssText = 'font-size:14px;color:var(--text,#111);line-height:1.6;margin-bottom:24px';
    // ── Stage-specific disclaimer ────────────────────────────────────────────
    const disclaimer = document.createElement('p'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    disclaimer.style.cssText = 'font-weight:600;margin-bottom:12px;color:var(--text,#111)';
    disclaimer.textContent =
        'DocAble is in Beta. This service is provided for evaluation and pilot use. '
            + 'Features may change without notice. There is no service-level agreement (SLA) '
            + 'during the Beta period. By using DocAble during Beta, you accept these terms.';
    // ── Canonical §7 IP-policy text (three paragraphs — user-ratified 2026-06-01) ──
    const p1 = document.createElement('p'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    p1.style.marginBottom = '10px';
    p1.textContent =
        'This system is provided for evaluation and pilot use only. No license is granted '
            + 'except the limited right to use the service through this interface.';
    const p2 = document.createElement('p'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    p2.style.marginBottom = '10px';
    p2.textContent =
        'I agree not to copy, modify, reverse engineer, scrape, benchmark for competitive '
            + 'purposes, redistribute, or create derivative works from the service, software, '
            + 'workflows, prompts, repair logic, reports, or documentation.';
    const p3 = document.createElement('p'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    p3.style.marginBottom = '10px';
    p3.textContent =
        'By uploading a document, you represent that you have the right to submit it for '
            + 'accessibility analysis and remediation. Do not upload regulated, confidential, '
            + 'export-controlled, health, student-record, or personally sensitive information '
            + 'unless you are authorized to do so.';
    // ── Data handling disclosure (design doc §3) ─────────────────────────────
    const dataP = document.createElement('p'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    dataP.style.cssText = 'font-size:13px;color:var(--text-muted,#555);margin-top:12px';
    dataP.textContent =
        'Data handling: DocAble processes uploaded documents on our servers and may send '
            + 'portions (images, text segments) to third-party GenAI providers (OpenAI, Anthropic, '
            + 'Google) for accessibility analysis. This is in beta, we are retaining files for 30 '
            + 'days and may use them to improve the system. We will not share them with other '
            + 'people or institutions without explicit consent.';
    body.append(disclaimer, p1, p2, p3, dataP);
    // ── Button row ───────────────────────────────────────────────────────────
    const btnRow = document.createElement('div'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    btnRow.style.cssText = 'display:flex;gap:12px;justify-content:flex-end';
    const declineBtn = document.createElement('button'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    declineBtn.type = 'button';
    declineBtn.id = 'adatool-consent-decline';
    declineBtn.textContent = 'Decline';
    declineBtn.setAttribute('aria-label', 'Decline — do not proceed with document submission');
    declineBtn.style.cssText = [
        'padding:10px 20px',
        'border:1px solid var(--border,#d1d5db)',
        'border-radius:6px',
        'background:var(--surface,#fff)',
        'color:var(--text,#111)',
        'font-size:14px',
        'cursor:pointer',
    ].join(';');
    const agreeBtn = document.createElement('button'); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    agreeBtn.type = 'button';
    agreeBtn.id = 'adatool-consent-agree';
    agreeBtn.textContent = 'I agree';
    agreeBtn.setAttribute('aria-label', 'I agree — I accept these terms and wish to proceed');
    agreeBtn.style.cssText = [
        'padding:10px 20px',
        'border:none',
        'border-radius:6px',
        'background:var(--accent,#2563eb)',
        'color:#fff', // #fff on #2563eb = 7.94:1 contrast ratio (WCAG AAA pass) // noqa: no-hardcoded-brand-color — contrast-verified a11y color
        'font-size:14px',
        'font-weight:600',
        'cursor:pointer',
    ].join(';');
    btnRow.append(declineBtn, agreeBtn);
    dialog.append(title, body, btnRow);
    overlay.appendChild(dialog);
    return overlay;
}
/** Return all focusable elements within the dialog, in DOM order. */
function _focusable(container) {
    const sel = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return [...container.querySelectorAll(sel)];
}
// ─── Public API ───────────────────────────────────────────────────────────────
/**
 * Show the consent dialog and wait for the user's decision.
 *
 * @returns Promise<boolean> — true = user accepted; false = declined / ESC.
 */
function _showDialog() {
    return new Promise((resolve) => {
        const overlay = _buildDialog();
        document.body.appendChild(overlay); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        const dialogEl = overlay.querySelector('[role="dialog"]');
        const declineBtn = overlay.querySelector('#adatool-consent-decline');
        const agreeBtn = overlay.querySelector('#adatool-consent-agree');
        // Focus the "I agree" button on open (primary action; per WCAG 2.4.3 focus order).
        agreeBtn.focus();
        function _close(accepted) {
            overlay.remove();
            if (keyHandler)
                document.removeEventListener('keydown', keyHandler); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            resolve(accepted);
        }
        // Focus trap: Tab/Shift+Tab cycle within the dialog.
        const keyHandler = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                _close(false);
                return;
            }
            if (e.key !== 'Tab')
                return;
            const focusable = _focusable(dialogEl);
            if (focusable.length === 0)
                return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey) {
                // Shift+Tab: if at first element, wrap to last.
                if (document.activeElement === first) { // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
                    e.preventDefault();
                    last.focus();
                }
            }
            else {
                // Tab: if at last element, wrap to first.
                if (document.activeElement === last) { // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', keyHandler); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        agreeBtn.addEventListener('click', () => _close(true));
        declineBtn.addEventListener('click', () => _close(false));
    });
}
/**
 * Gate the caller on consent if the release stage requires it.
 *
 * @param releaseStage  The deployment's release stage (from /api/auth/me).
 * @returns Promise<boolean>  true = proceed; false = user declined or stage unknown.
 */
export async function requireConsentIfNeeded(releaseStage) {
    const stage = (releaseStage || '').toUpperCase();
    // Stage does not require a consent gate — proceed immediately.
    if (!CONSENT_REQUIRED_STAGES.has(stage))
        return true;
    // User already consented to the current version — proceed immediately.
    if (_hasValidConsent())
        return true;
    // Show the dialog and wait for the user's decision.
    const accepted = await _showDialog();
    if (accepted) {
        _writeConsentCookie(CONSENT_VERSION);
    }
    return accepted;
}
